<template>
  <div class="h-screen flex flex-col overflow-hidden">
    <!-- Sign In Screen -->
    <div
      v-if="!user"
      class="h-screen flex flex-col items-center justify-center bg-gray-50 px-4"
    >
      <h1 class="text-3xl font-bold mb-6">Please Reply - BRX</h1>
      <Button
        @click="signInWithGoogle"
        class="w-full max-w-xs bg-black text-white hover:bg-gray-900"
      >
        Sign In with Google
      </Button>
    </div>

    <!-- Main App (shown when signed in) -->
    <div v-else class="flex flex-col flex-1 overflow-hidden">
      <!-- Header -->
      <header class="bg-white shadow flex items-center justify-between px-4 py-2 flex-shrink-0">
        <div class="flex items-center">
          <!-- Replace this SVG with your own logo if needed -->
          <svg
            class="h-8 w-8 text-black mr-2"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M20,4H4C2.9,4,2,4.9,2,6v12c0,1.1,0.9,2,2,2h16c1.1,0,2-0.9,2-2V6C22,4.9,21.1,4,20,4z M20,6v2l-8,5l-8-5V6 L12,11L20,6z M4,18V9l8,5l8-5v9H4z"
            />
          </svg>
          <span class="font-bold text-xl">Please Reply</span>
        </div>
        <div class="flex-1 mx-4">
          <!-- Use @click to open the command palette -->
          <input
            type="text"
            placeholder="Search messages"
            class="w-full border border-gray-300 rounded-full px-3 py-1 focus:outline-none focus:ring-2 focus:ring-black"
            @click="openSentEmailsPalette"
          />
        </div>
        <div>
          <Button
            @click="logout"
            class="bg-black text-white hover:bg-gray-900"
          >
            Logout
          </Button>
        </div>
      </header>

      <!-- Body -->
      <div class="flex flex-1 overflow-hidden">
        <!-- Sidebar: Navigation + Always-open BRK Panel -->
        <aside class="w-64 bg-gray-100 p-4 border-r flex-shrink-0 overflow-y-auto scrollbar-hide">
          <Button
            @click="toggleCompose"
            class="mb-4 w-full bg-black text-white hover:bg-gray-900"
          >
            Compose
          </Button>
          <nav>
            <ul>
              <li
                class="mb-2 cursor-pointer hover:bg-gray-200 p-2 rounded"
                :class="{ 'bg-gray-300': activeFolder === 'inbox' }"
                @click="setFolder('inbox')"
              >
                Inbox
              </li>
              <li
                class="mb-2 cursor-pointer hover:bg-gray-200 p-2 rounded"
                :class="{ 'bg-gray-300': activeFolder === 'sent' }"
                @click="setFolder('sent')"
              >
                Sent
              </li>
            </ul>
          </nav>
          <!-- Always-open BRK Configuration Panel -->
          <div class="mt-6">
            <h2 class="font-bold text-lg mb-2">BRK Configuration</h2>
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                BRX API Key
              </label>
              <div class="flex gap-2">
                <Input
                  v-model="brxApiKey"
                  type="password"
                  placeholder="Enter BRX API Key..."
                  class="flex-grow border border-black text-black"
                />
                <Button
                  @click="saveBrxApiKey"
                  :disabled="configLoading"
                  class="bg-black text-white hover:bg-gray-900"
                >
                  Save
                </Button>
              </div>
            </div>
            <div class="mb-4">
              <Button
                @click="startGmailWatch"
                class="w-full bg-black text-white hover:bg-gray-900"
              >
                Start Watch
              </Button>
            </div>
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                BRK ID
              </label>
              <div class="flex gap-2 mb-2">
                <Input
                  v-model="localBrkId"
                  @input="handleInputUpdate"
                  placeholder="Enter BRK ID..."
                  class="flex-grow border border-black text-black"
                />
                <Button
                  @click="handleFetch"
                  :disabled="configLoading"
                  class="bg-black text-white hover:bg-gray-900 flex items-center"
                >
                  <RefreshCw
                    class="w-4 h-4 mr-2"
                    :class="{ 'animate-spin': configLoading }"
                  />
                  Load
                </Button>
                <Button
                  @click="openInBRX"
                  class="bg-black text-white hover:bg-gray-900"
                >
                  <ExternalLink class="w-4 h-4" />
                </Button>
              </div>
              <DynamicInputFields
                :fields="dynamicFields"
                :modelValue="userDynamicInputs"
                :isLoading="configLoading"
                @update:modelValue="handleDynamicInputsUpdate"
              />
              <Button
                @click="saveConfig"
                class="mt-4 w-full bg-black text-white hover:bg-gray-900"
              >
                Save Configuration
              </Button>
            </div>
          </div>
        </aside>

        <!-- Main Content -->
        <main class="flex flex-1 overflow-hidden">
          <!-- Left Pane: Email List -->
          <section class="w-1/3 border-r overflow-y-auto scrollbar-hide">
            <!-- Inbox View -->
            <div v-if="activeFolder === 'inbox'">
              <div class="flex items-center justify-between px-4 py-2 border-b">
                <h2 class="font-bold text-lg">Inbox</h2>
                <Button
                  @click="fetchInboxEmails"
                  class="bg-black text-white hover:bg-gray-900"
                >
                  Refresh
                </Button>
              </div>
              <ul>
                <li
                  v-for="email in inboxEmails"
                  :key="email.id"
                  @click="selectEmail(email)"
                  class="cursor-pointer hover:bg-gray-100 p-4 border-b"
                >
                  <p class="font-semibold">{{ email.subject }}</p>
                  <p class="text-sm text-gray-600">
                    <span v-if="email.from">From: {{ email.from }}</span>
                    <span v-else>{{ email.snippet }}</span>
                  </p>
                </li>
              </ul>
            </div>
            <!-- Sent View -->
            <div v-else-if="activeFolder === 'sent'">
              <div class="flex items-center justify-between px-4 py-2 border-b">
                <h2 class="font-bold text-lg">Sent Mail</h2>
                <Button
                  @click="fetchSentEmails"
                  class="bg-black text-white hover:bg-gray-900"
                >
                  Refresh
                </Button>
              </div>
              <ul>
                <li
                  v-for="email in sentEmails"
                  :key="email.id"
                  @click="selectEmail(email)"
                  class="cursor-pointer hover:bg-gray-100 p-4 border-b"
                >
                  <p class="font-semibold">{{ email.subject }}</p>
                  <p class="text-sm text-gray-600">{{ email.snippet }}</p>
                </li>
              </ul>
            </div>
          </section>

          <!-- Right Pane: Email Thread / Compose Modal -->
          <section class="flex-1 overflow-y-auto relative p-4 scrollbar-hide">
            <!-- Compose Modal -->
            <div
              v-if="showCompose"
              class="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10"
            >
              <div class="bg-white rounded shadow-lg w-1/2 p-6 relative">
                <button
                  @click="toggleCompose"
                  class="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                >
                  X
                </button>
                <h2 class="text-xl font-bold mb-4">Compose Email</h2>
                <form @submit.prevent="sendEmail" class="space-y-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700">To</label>
                    <Input
                      v-model="emailForm.to"
                      placeholder="Recipient's email"
                      required
                      class="w-full border border-black text-black"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700">
                      Subject
                    </label>
                    <Input
                      v-model="emailForm.subject"
                      placeholder="Email subject"
                      required
                      class="w-full border border-black text-black"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700">
                      Message
                    </label>
                    <textarea
                      v-model="emailForm.messageText"
                      required
                      rows="5"
                      placeholder="Your message here..."
                      class="mt-1 block w-full rounded-md border border-black text-black shadow-sm focus:border-black focus:ring-black"
                    ></textarea>
                  </div>
                  <div class="flex justify-end">
                    <Button
                      type="submit"
                      class="bg-black text-white hover:bg-gray-900"
                    >
                      Send
                    </Button>
                  </div>
                </form>
              </div>
            </div>

            <!-- Email Thread Display -->
            <div v-if="selectedThread" class="space-y-4">
              <h2 class="font-bold text-xl mb-4">Conversation</h2>
              <div
                v-for="msg in selectedThread.messages"
                :key="msg.id"
                class="p-4 border rounded"
              >
                <div class="mb-2">
                  <span class="font-semibold">
                    {{ getHeader(msg.payload.headers, 'From') }}
                  </span>
                  <span class="text-gray-600"> to </span>
                  <span class="font-semibold">
                    {{ getHeader(msg.payload.headers, 'To') }}
                  </span>
                </div>
                <div class="text-sm text-gray-500 mb-2">
                  {{ formatDate(msg.internalDate) }}
                </div>
                <div>
                  <p>{{ msg.snippet }}</p>
                </div>
              </div>
            </div>
            <div v-else class="text-gray-500">
              Select an email to view the conversation.
            </div>
          </section>
        </main>
      </div>

      <!-- Footer / Status Message -->
      <footer
        class="p-2 text-center text-sm flex-shrink-0"
        :class="message.includes('Error') ? 'text-red-600' : 'text-green-600'"
      >
        {{ message }}
      </footer>

      <!-- Include the Sent Emails Command Palette -->
      <CommandPaletteSentEmails
        ref="sentEmailsPalette"
        :emails="sentEmails"
        @select="selectEmail"
        @close="closeSentEmailsPalette"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
  signOut,
} from 'firebase/auth'
import {
  getFirestore,
  collection,
  addDoc,
  setDoc,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import DynamicInputFields from '@/components/codeBrk/DynamicInputFields.vue'
import { RefreshCw, ExternalLink } from 'lucide-vue-next'
import BRX from 'brx-node'
import CommandPaletteSentEmails from '@/components/CommandPaletteSentEmails.vue'

// Email and configuration state
const user = ref<any>(null)
const googleAccessToken = ref<string>('')

const emailForm = ref({
  to: '',
  subject: '',
  messageText: '',
})

const message = ref('')
const sentEmails = ref<any[]>([])
const inboxEmails = ref<any[]>([])
const selectedThread = ref<any | null>(null)

const auth = getAuth()
const db = getFirestore()

// BRK/BRX configuration state
const brkId = ref('')
const localBrkId = ref('')
const dynamicFields = ref<string[]>(['customGreeting', 'signature'])
const userDynamicInputs = ref<Record<string, string>>({})
const configLoading = ref(false)
const brxApiKey = ref('')

const brxInstance = ref<any>(null)

// Reference to the sent-emails command palette
const sentEmailsPalette = ref(null)

async function initializeBrx(apiKey: string) {
  try {
    const instance = new BRX(apiKey, { verbose: true, send_local: false })
    return instance
  } catch (error) {
    console.error('Error initializing BRX:', error)
    message.value = 'Failed to initialize BRX client'
    throw error
  }
}

async function rehydrateUserConfig() {
  try {
    const userDocRef = doc(db, 'REPLY_USER', user.value.uid)
    const userDocSnap = await getDoc(userDocRef)
    if (userDocSnap.exists()) {
      const data = userDocSnap.data()
      if (data.brkId) {
        brkId.value = data.brkId
      }
      if (data.dynamicInputs) {
        userDynamicInputs.value = data.dynamicInputs
      }
      if (data.brxApiKey) {
        brxApiKey.value = data.brxApiKey
        brxInstance.value = await initializeBrx(data.brxApiKey)
      }
    }
  } catch (error) {
    console.error('Error rehydrating user config:', error)
  }
}

onMounted(async () => {
  try {
    brxInstance.value = await initializeBrx(brxApiKey.value || "your-brx-api-key")
  } catch (error) {
    console.error('BRX client failed to initialize', error)
  }
  onAuthStateChanged(auth, async (currentUser) => {
    user.value = currentUser
    if (currentUser) {
      await rehydrateUserConfig()
      if (googleAccessToken.value) {
        if(activeFolder.value === 'sent'){
          fetchSentEmails()
        } else {
          fetchInboxEmails()
        }
      }
    }
  })
})

watch(localBrkId, (newVal) => {
  brkId.value = newVal
})
watch(brkId, (newVal) => {
  localBrkId.value = newVal
}, { immediate: true })

watch(brkId, async (newId, oldId) => {
  if (newId && newId !== oldId) {
    await fetchBrkInfo()
  }
})

watch(googleAccessToken, (newVal) => {
  if (newVal) {
    if(activeFolder.value === 'sent'){
      fetchSentEmails()
    } else {
      fetchInboxEmails()
    }
  }
})

async function signInWithGoogle() {
  const provider = new GoogleAuthProvider()
  provider.addScope('https://www.googleapis.com/auth/gmail.send')
  provider.addScope('https://www.googleapis.com/auth/gmail.readonly')
  try {
    const result = await signInWithPopup(auth, provider)
    const credential = GoogleAuthProvider.credentialFromResult(result)
    if (credential && credential.accessToken) {
      googleAccessToken.value = credential.accessToken
      user.value = result.user
      message.value = `Logged in as ${user.value.email}`
      await setDoc(
        doc(db, 'users', user.value.uid),
        {
          gmailAccessToken: googleAccessToken.value,
          lastHistoryId: null,
          email: user.value.email,
          uid: user.value.uid,
        },
        { merge: true }
      )
    } else {
      message.value = 'No access token received.'
    }
  } catch (error) {
    console.error('Error signing in with Google', error)
    message.value = 'Error signing in with Google'
  }
}

async function logout() {
  try {
    await signOut(auth)
    user.value = null
    googleAccessToken.value = ''
    message.value = 'Logged out successfully'
  } catch (error) {
    console.error('Error logging out', error)
    message.value = 'Error logging out'
  }
}

function makeRawEmail(to: string, subject: string, body: string): string {
  const emailContent = [
    // `From: Please Reply - BRX <${user.value.email}>`, // I want this to work 
    `To: <${to}>`,
    `Subject: ${subject}`,
    '',
    body,
  ].join('\n')
  return btoa(emailContent)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

async function sendEmail() {
  if (!googleAccessToken.value) {
    message.value = 'You must sign in with Google first.'
    return
  }
  const raw = makeRawEmail(
    emailForm.value.to,
    emailForm.value.subject,
    emailForm.value.messageText
  )
  try {
    const response = await fetch(
      'https://gmail.googleapis.com/gmail/v1/users/me/messages/send',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + googleAccessToken.value,
        },
        body: JSON.stringify({ raw }),
      }
    )
    const data = await response.json()
    if (response.ok) {
      message.value = 'Email sent! Message Id: ' + data.id
      if (user.value) {
        await addDoc(collection(db, 'REPLY_USER', user.value.uid, 'sent'), {
          to: emailForm.value.to,
          subject: emailForm.value.subject,
          messageText: emailForm.value.messageText,
          timestamp: serverTimestamp(),
          gmailMessageId: data.id,
          threadId: data.threadId,
        })
      }
      emailForm.value.to = ''
      emailForm.value.subject = ''
      emailForm.value.messageText = ''
      if(activeFolder.value === 'sent'){
        fetchSentEmails()
      } else {
        fetchInboxEmails()
      }
      toggleCompose()
    } else {
      message.value =
        'Error sending email: ' + (data.error?.message || data.error)
    }
  } catch (error) {
    console.error('Error sending email', error)
    message.value = 'Error sending email'
  }
}

async function fetchEmails(label: string) {
  try {
    const response = await fetch(
      `https://gmail.googleapis.com/gmail/v1/users/me/messages?labelIds=${label}&maxResults=10`,
      {
        headers: {
          'Authorization': 'Bearer ' + googleAccessToken.value,
        },
      }
    )
    const listData = await response.json()
    if (!listData.messages) {
      return []
    }
    const emails = await Promise.all(
      listData.messages.map(async (msg: any) => {
        const msgResponse = await fetch(
          `https://gmail.googleapis.com/gmail/v1/users/me/messages/${msg.id}?format=metadata&metadataHeaders=Subject&metadataHeaders=From&metadataHeaders=To`,
          {
            headers: {
              'Authorization': 'Bearer ' + googleAccessToken.value,
            },
          }
        )
        const msgData = await msgResponse.json()
        let subject = ''
        let from = ''
        let to = ''
        if (msgData.payload && msgData.payload.headers) {
          msgData.payload.headers.forEach((header: any) => {
            if (header.name === 'Subject') subject = header.value
            if (header.name === 'From') from = header.value
            if (header.name === 'To') to = header.value
          })
        }
        return {
          id: msgData.id,
          threadId: msgData.threadId,
          subject,
          from,
          to,
          snippet: msgData.snippet,
        }
      })
    )
    return emails
  } catch (error) {
    console.error('Error fetching emails:', error)
    message.value = 'Error fetching emails'
    return []
  }
}

async function fetchSentEmails() {
  if (!googleAccessToken.value) {
    message.value = 'You must sign in with Google first.'
    return
  }
  sentEmails.value = await fetchEmails('SENT')
}

async function fetchInboxEmails() {
  if (!googleAccessToken.value) {
    message.value = 'You must sign in with Google first.'
    return
  }
  inboxEmails.value = await fetchEmails('INBOX')
}

async function selectEmail(email: any) {
  selectedThread.value = null
  try {
    const response = await fetch(
      `https://gmail.googleapis.com/gmail/v1/users/me/threads/${email.threadId}?format=full`,
      {
        headers: {
          'Authorization': 'Bearer ' + googleAccessToken.value,
        },
      }
    )
    const threadData = await response.json()
    if (response.ok) {
      selectedThread.value = threadData
    } else {
      message.value =
        'Error loading thread: ' +
        (threadData.error?.message || threadData.error)
    }
  } catch (error) {
    console.error('Error loading email thread', error)
    message.value = 'Error loading email thread'
  }
}

function getHeader(headers: any[], name: string): string {
  const header = headers.find((h) => h.name === name)
  return header ? header.value : ''
}

function formatDate(timestamp: string): string {
  const date = new Date(parseInt(timestamp))
  return date.toLocaleString()
}

async function startGmailWatch() {
  if (!googleAccessToken.value) {
    message.value = 'You must sign in with Google first.'
    return
  }
  try {
    const res = await fetch(
      'https://gmail.googleapis.com/gmail/v1/users/me/watch',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + googleAccessToken.value,
        },
        body: JSON.stringify({
          topicName: 'projects/brx-please-reply/topics/gmailNotifications',
          labelIds: ['INBOX'],
          labelFilterBehavior: 'INCLUDE',
        }),
      }
    )
    const data = await res.json()
    if (res.ok) {
      message.value = 'Watch started. History ID: ' + data.historyId
      await updateDoc(doc(db, 'users', user.value.uid), {
        lastHistoryId: data.historyId,
      })
    } else {
      message.value =
        'Error starting watch: ' + (data.error?.message || data.error)
    }
  } catch (error) {
    console.error('Error calling watch:', error)
    message.value = 'Error calling watch'
  }
}

function handleInputUpdate(event: Event) {
  const value = (event.target as HTMLInputElement).value
  localBrkId.value = value
}

async function handleFetch() {
  if (configLoading.value) return
  configLoading.value = true
  try {
    await fetchBrkInfo()
  } catch (error) {
    console.error('Failed to fetch BRK configuration', error)
  } finally {
    configLoading.value = false
  }
}

async function fetchBrkInfo() {
  if (!brkId.value) return
  if (!brxInstance.value) {
    message.value = 'BRX client not initialized.'
    return
  }
  try {
    const brxRequest = await brxInstance.value.get(brkId.value)
    dynamicFields.value = Object.keys(brxRequest.input || {})
    const newInputs: Record<string, string> = {}
    dynamicFields.value.forEach((field) => {
      newInputs[field] =
        userDynamicInputs.value[field] || brxRequest.input[field] || ''
    })
    userDynamicInputs.value = newInputs
    message.value = 'BRK configuration loaded successfully'
  } catch (error) {
    console.error('Error fetching BRK info:', error)
    message.value = 'Error fetching BRK configuration'
  }
}

function handleDynamicInputsUpdate(value: Record<string, string>) {
  userDynamicInputs.value = value
}

async function saveConfig() {
  if (!user.value) {
    message.value = 'You must sign in to save configuration'
    return
  }
  configLoading.value = true
  try {
    await setDoc(
      doc(db, 'REPLY_USER', user.value.uid),
      {
        brkId: brkId.value,
        dynamicInputs: userDynamicInputs.value,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    )
    message.value = 'Configuration saved successfully'
  } catch (error) {
    console.error('Error saving configuration', error)
    message.value = 'Error saving configuration'
  } finally {
    configLoading.value = false
  }
}

async function saveBrxApiKey() {
  if (!user.value) {
    message.value = 'You must sign in to save the BRX API key'
    return
  }
  if (!brxApiKey.value) {
    message.value = 'Please enter a BRX API key'
    return
  }
  configLoading.value = true
  try {
    await setDoc(
      doc(db, 'REPLY_USER', user.value.uid),
      {
        brxApiKey: brxApiKey.value,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    )
    brxInstance.value = await initializeBrx(brxApiKey.value)
    message.value = 'BRX API key saved successfully'
  } catch (error) {
    console.error('Error saving BRX API key', error)
    message.value = 'Error saving BRX API key'
  } finally {
    configLoading.value = false
  }
}

function openInBRX() {
  if (!brkId.value) return
  const url = `https://app.brx.ai/brk/builder/${brkId.value}`
  window.open(url, '_blank')
}

const showCompose = ref(false)
const activeFolder = ref('sent')

function toggleCompose() {
  showCompose.value = !showCompose.value
}

function setFolder(folder: string) {
  activeFolder.value = folder
  if (folder === 'sent') {
    fetchSentEmails()
  } else if (folder === 'inbox') {
    fetchInboxEmails()
  }
}

// Command Palette handlers for sent emails
function openSentEmailsPalette() {
  sentEmailsPalette.value?.openCommandPalette()
}

function closeSentEmailsPalette() {
  // Additional actions can be added here if needed when the palette closes.
}
</script>

<style scoped>
/* Hide scrollbars on elements with .scrollbar-hide */
.scrollbar-hide {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;  /* Chrome, Safari and Opera */
}
</style>
